import { Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { ComparisonDiffEntry } from "./comparison-grouped-value.types.ts"

const { Text } = Typography

interface MilestoneChangesPanelProps {
  entries: ComparisonDiffEntry[]
  /** Already-translated "Proposal · V2 · Approved compared with the Lead". */
  title: string
  testIdPrefix: string
}

interface ChangeGroup {
  id: string
  name: string
  entries: ComparisonDiffEntry[]
}

function groupByProjectType(entries: ComparisonDiffEntry[]): ChangeGroup[] {
  const order: string[] = []
  const groups = new Map<string, ChangeGroup>()
  entries.forEach((entry) => {
    const id = entry.groupId ?? entry.groupName
    if (!groups.has(id)) {
      groups.set(id, { id, name: entry.groupName, entries: [] })
      order.push(id)
    }
    groups.get(id)!.entries.push(entry)
  })
  return order.map((id) => groups.get(id)!)
}

/**
 * The full-width "View changes" view for a Scope row: every added, removed
 * and changed milestone, kept under the project type it belongs to, one line
 * per attribute that actually moved. Rendered as its own row spanning the
 * whole comparison table rather than inside a one-third-width cell, and with
 * no height cap of its own — it scrolls with the table, so the modal never
 * grows a second scrollbar inside the first.
 */
function MilestoneChangesPanel({
  entries,
  title,
  testIdPrefix
}: MilestoneChangesPanelProps) {
  const { t } = useTranslation()
  const empty = t("documentSync.groupedComparison.notSet")

  return (
    <div
      className="rounded-md border border-gray-200 bg-gray-50 p-3"
      data-testid={`${testIdPrefix}-changes-panel`}
    >
      <Text strong className="block mb-2">
        {title}
      </Text>
      {groupByProjectType(entries).map((group) => (
        <div key={group.id} className="mb-3 last:mb-0">
          <Text
            type="secondary"
            className="block text-xs font-semibold uppercase tracking-wide mb-1"
            data-testid={`${testIdPrefix}-changes-group-${group.id}`}
          >
            {t("documentSync.groupedComparison.changesGroupHeader", {
              groupName: group.name,
              count: group.entries.length
            })}
          </Text>
          <table className="w-full table-fixed text-sm border-collapse bg-white rounded">
            <colgroup>
              <col style={{ width: "34%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "25%" }} />
            </colgroup>
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-1.5 px-2 font-medium">
                  {t("documentSync.groupedComparison.columnMilestone")}
                </th>
                <th className="py-1.5 px-2 font-medium">
                  {t("documentSync.groupedComparison.columnChange")}
                </th>
                <th className="py-1.5 px-2 font-medium">
                  {t("documentSync.groupedComparison.columnBefore")}
                </th>
                <th className="py-1.5 px-2 font-medium">
                  {t("documentSync.groupedComparison.columnAfter")}
                </th>
              </tr>
            </thead>
            <tbody>
              {group.entries.map((entry) => {
                const label = entry.after?.label ?? entry.before?.label ?? ""
                if (entry.status === "added" || entry.status === "removed") {
                  const added = entry.status === "added"
                  return (
                    <tr
                      key={entry.id}
                      className="border-t border-gray-100 align-top"
                      data-testid={`${testIdPrefix}-diff-${entry.id}`}
                    >
                      <td className="py-1.5 px-2 break-words">{label}</td>
                      <td className="py-1.5 px-2">
                        <Tag color={added ? "green" : "red"} className="m-0">
                          {t(`documentSync.groupedComparison.${entry.status}`)}
                        </Tag>
                      </td>
                      <td className="py-1.5 px-2 break-words text-gray-600">
                        {added ? empty : entry.before?.detail || label}
                      </td>
                      <td className="py-1.5 px-2 break-words">
                        {added ? entry.after?.detail || label : empty}
                      </td>
                    </tr>
                  )
                }

                const changes = entry.changes?.length
                  ? entry.changes
                  : [
                      {
                        attribute: t("documentSync.groupedComparison.changed"),
                        before: entry.before?.detail ?? empty,
                        after: entry.after?.detail ?? empty
                      }
                    ]
                return changes.map((change, index) => (
                  <tr
                    key={`${entry.id}-${change.attribute}`}
                    className={
                      "align-top" +
                      (index === 0 ? " border-t border-gray-100" : "")
                    }
                    data-testid={
                      index === 0
                        ? `${testIdPrefix}-diff-${entry.id}`
                        : undefined
                    }
                  >
                    <td className="py-1.5 px-2 break-words">
                      {index === 0 ? label : null}
                    </td>
                    <td className="py-1.5 px-2 text-gray-600">
                      {change.attribute}
                    </td>
                    <td className="py-1.5 px-2 break-words text-gray-600 line-through decoration-gray-300">
                      {change.before}
                    </td>
                    <td className="py-1.5 px-2 break-words font-medium">
                      {change.after}
                    </td>
                  </tr>
                ))
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

export { MilestoneChangesPanel }
